/**
 * MINI-JEU — RUNNER UN-TAP. Le personnage avance tout seul ; tap = saut pour éviter
 * les obstacles qui arrivent de droite. 3 vies, 15 obstacles = victoire + créature.
 * Physique normalisée (unités = fraction de la hauteur écran). Thémé par enfant :
 * Isaac = dino 🦖 + cactus, Bao = chat 🐱 + vagues kawaii.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getChildId } from "./childSession";
import { createGameAudio, type GameAudio } from "./gameSounds";

const TARGET = 15;
const GROUND_Y = 0.76;    // position du sol (ratio de h)
const RUNNER_X = 0.2;     // position x fixe du runner
const GRAVITY  = 0.0000042; // accélération gravité (unités normalisées / ms²)
const JUMP_VY  = -0.00148;  // vitesse initiale saut (négatif = vers le haut)
// → pic ≈ 26 % de la hauteur, durée totale ≈ 700 ms

type Drop = { emoji: string; name: string; rarity: string } | null;
type Obs  = { x: number; h: number; w: number; emoji: string; cleared: boolean };

function themeFor(cid: string | null) {
  return cid === "bao"
    ? { runner: "🐱", obstacles: ["🌊", "🌊", "💫", "🎀"], color: "#ff7ac6" }
    : { runner: "🦖", obstacles: ["🌵", "🌵", "⚡", "🦴"], color: "#00d4ff" };
}

export default function RunnerGame({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [score,  setScore]  = useState(0);
  const [lives,  setLives]  = useState(3);
  const [level,  setLevel]  = useState(1);
  const [status, setStatus] = useState<"play" | "won" | "lost">("play");
  const [reward, setReward] = useState<Drop>(null);

  const g = useRef({
    obs: [] as Obs[], runnerY: 0, vy: 0, onGround: true,
    w: 0, h: 0, lastSpawn: 0, raf: 0,
    score: 0, lives: 3, over: false, hitCooldown: 0,
    theme: themeFor(getChildId()),
  });
  const sfx = useRef<GameAudio | null>(null);
  if (!sfx.current) sfx.current = createGameAudio();
  useEffect(() => () => sfx.current?.dispose(), []);

  const finish = useCallback(async (won: boolean) => {
    g.current.over = true;
    cancelAnimationFrame(g.current.raf);
    setStatus(won ? "won" : "lost");
    sfx.current?.[won ? "win" : "lose"]();
    if (won) {
      try {
        const r = await fetch(`${apiBase}/api/child/minigame/reward`, { method: "POST" });
        const d = await r.json();
        if (d?.drop) setReward(d.drop);
      } catch { /* pas de récompense hors-ligne */ }
    }
  }, [apiBase]);

  const jump = useCallback(() => {
    const st = g.current;
    if (st.onGround && !st.over) {
      st.vy = JUMP_VY;
      st.onGround = false;
      sfx.current?.jump();
    }
  }, []);

  const start = useCallback(() => {
    const st = g.current;
    st.obs = []; st.runnerY = 0; st.vy = 0; st.onGround = true;
    st.lastSpawn = 0; st.score = 0; st.lives = 3; st.over = false; st.hitCooldown = 0;
    st.theme = themeFor(getChildId());
    setScore(0); setLives(3); setLevel(1); setReward(null); setStatus("play");
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d")!;

    const getAccent = () =>
      (getComputedStyle(document.documentElement).getPropertyValue("--kid-cyan") || "#00d4ff").trim();

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      g.current.w = cvs.clientWidth;
      g.current.h = cvs.clientHeight;
      cvs.width  = g.current.w * dpr;
      cvs.height = g.current.h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let last = performance.now();

    const loop = (t: number) => {
      const st = g.current;
      if (st.over) return;
      const dt = Math.min(50, t - last); last = t;
      const { w, h, theme } = st;

      // niveau par palier (Barkley)
      const lv = st.score < 5 ? 1 : st.score < 10 ? 2 : 3;
      if (lv !== g.current.score) setLevel(lv); // on re-setLevel proprement plus bas

      const P = lv === 1
        ? { speed: 0.00025, spawnMs: 2100, obsH: 0.09 }
        : lv === 2
        ? { speed: 0.00032, spawnMs: 1750, obsH: 0.11 }
        : { speed: 0.00039, spawnMs: 1500, obsH: 0.12 };

      // physique runner (runnerY = 0 au sol, négatif = en l'air)
      st.vy      += GRAVITY * dt;
      st.runnerY += st.vy * dt;
      if (st.runnerY >= 0) { st.runnerY = 0; st.vy = 0; st.onGround = true; }

      // spawn obstacle
      if (t - st.lastSpawn > P.spawnMs) {
        st.lastSpawn = t;
        const emoji = theme.obstacles[Math.floor(Math.random() * theme.obstacles.length)];
        st.obs.push({ x: 1.06, h: P.obsH, w: 0.07, emoji, cleared: false });
      }

      // déplacement + collision + score
      if (st.hitCooldown > 0) st.hitCooldown -= dt;
      for (const o of st.obs) {
        o.x -= P.speed * dt;

        // score : obstacle dépassé
        if (!o.cleared && o.x + o.w < RUNNER_X - 0.06) {
          o.cleared = true;
          st.score++;
          setScore(st.score);
          sfx.current?.point();
          setLevel(st.score < 5 ? 1 : st.score < 10 ? 2 : 3);
          if (st.score >= TARGET) { finish(true); return; }
        }

        // collision (hitbox réduite pour être fair)
        if (!o.cleared && st.hitCooldown <= 0) {
          const runnerBottom = GROUND_Y + st.runnerY; // pied du runner (normalisé)
          const xHit = RUNNER_X + 0.035 > o.x && RUNNER_X - 0.035 < o.x + o.w;
          const yHit = runnerBottom > GROUND_Y - o.h + 0.015; // n'a pas sauté assez haut
          if (xHit && yHit) {
            st.lives--;
            setLives(st.lives);
            sfx.current?.hit();
            st.hitCooldown = 900;
            o.cleared = true;
            if (st.lives <= 0) { finish(false); return; }
          }
        }
      }
      st.obs = st.obs.filter(o => o.x > -0.12);

      // --- rendu ---
      ctx.clearRect(0, 0, w, h);
      ctx.textAlign = "center";

      // ligne de sol
      const accent = getAccent();
      ctx.save();
      ctx.globalAlpha = 0.22;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y * h + 6);
      ctx.lineTo(w, GROUND_Y * h + 6);
      ctx.stroke();
      ctx.restore();

      // obstacles
      ctx.font = "38px system-ui";
      ctx.textBaseline = "bottom";
      for (const o of st.obs) {
        if (o.cleared) continue;
        ctx.fillText(o.emoji, (o.x + o.w / 2) * w, GROUND_Y * h + 8);
      }

      // runner (clignote pendant l'invincibilité)
      const blink = st.hitCooldown > 0 && Math.floor(st.hitCooldown / 110) % 2 === 0;
      if (!blink) {
        ctx.font = "44px system-ui";
        ctx.textBaseline = "bottom";
        ctx.fillText(theme.runner, RUNNER_X * w, (GROUND_Y + st.runnerY) * h + 8);
      }

      // compteur de vitesse discret
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.font = "10px monospace";
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      ctx.fillStyle = accent;
      ctx.restore();

      st.raf = requestAnimationFrame(loop);
    };
    g.current.raf = requestAnimationFrame(loop);

    const onTap = (e: PointerEvent) => { e.preventDefault(); jump(); };
    cvs.addEventListener("pointerdown", onTap);

    return () => {
      cancelAnimationFrame(g.current.raf);
      window.removeEventListener("resize", resize);
      cvs.removeEventListener("pointerdown", onTap);
    };
  }, [status, finish, jump]);

  const rar = reward?.rarity;
  const rewardCol = rar === "legendary" ? "#ffd43b" : rar === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>NIVEAU {level} · {score} / {TARGET}</div>
        <div style={S.lives}>{"❤️".repeat(Math.max(0, lives))}</div>
      </div>

      <div style={S.boardWrap}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", touchAction: "none", display: "block" }}
        />
      </div>

      <div style={S.hint}>
        {g.current.theme.runner} Tape n'importe où pour sauter · évite les obstacles
      </div>

      {status !== "play" && (
        <div style={S.overlay} onClick={start}>
          <div style={S.card}>
            <div style={{ fontSize: 42 }}>{status === "won" ? "🏆" : "💥"}</div>
            <div style={{ color: status === "won" ? "var(--kid-cyan)" : "#ff6b6b", fontWeight: 800, fontSize: 21 }}>
              {status === "won" ? "GAGNÉ !" : "PERDU !"}
            </div>
            {status === "won" && reward && (
              <div style={{ ...S.rewardBox, borderColor: rewardCol }}>
                <div style={{ fontSize: 44 }}>{reward.emoji}</div>
                <div style={{ fontWeight: 700 }}>{reward.name}</div>
                <div style={{ color: rewardCol, fontSize: 12, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em" }}>
                  CRÉATURE DÉBLOQUÉE !
                </div>
              </div>
            )}
            {status === "won" && !reward && (
              <div style={S.sub}>Excellent ! Rejoue pour tenter une créature.</div>
            )}
            {status === "lost" && <div style={S.sub}>Presque ! On retente ?</div>}
            <button style={S.replay} onClick={(e) => { e.stopPropagation(); start(); }}>
              🔄 Rejouer
            </button>
            <button style={S.leave} onClick={(e) => { e.stopPropagation(); onClose(); }}>
              Quitter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen:   { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header:   { display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close:    { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title:    { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.12em", fontSize: 14, color: "var(--kid-cyan)" },
  lives:    { fontSize: 16 },
  boardWrap:{ flex: 1, position: "relative", overflow: "hidden" },
  hint:     { textAlign: "center", color: "var(--kid-text-3)", fontSize: 12, padding: "8px 16px calc(env(safe-area-inset-bottom) + 8px)" },
  overlay:  { position: "fixed", inset: 0, zIndex: 64, background: "rgba(3,5,9,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  card:     { textAlign: "center", padding: "26px 30px", borderRadius: 20, border: "1px solid var(--kid-cyan-40)", background: "var(--kid-bg)", maxWidth: 340, width: "100%" },
  sub:      { color: "var(--kid-text-3)", fontSize: 13, marginTop: 6 },
  rewardBox:{ margin: "16px auto 4px", padding: "14px", borderRadius: 16, border: "2px solid var(--kid-cyan)", display: "flex", flexDirection: "column", gap: 4, alignItems: "center" },
  replay:   { display: "block", width: "100%", marginTop: 18, padding: "13px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  leave:    { display: "block", width: "100%", marginTop: 10, padding: "11px", borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-text-2)", fontSize: 14, cursor: "pointer" },
};
